import { Fragment, type ReactNode } from 'react';
import { GRANT_SUPPORT_FILTERS, PROGRAM_KINDS, PROGRAM_KIND_LABEL, SUPPORT_TAB_LABEL, type GrantSupportFilter, type ProgramKind } from '@zoxo/shared';

import { PILL_TAB, PILL_TAB_HIGHLIGHT, PILL_TAB_OFF, PILL_TAB_SLIDING_ON } from '../../components/ui/pill-tab';
import { cn } from '../../lib/utils';
import { programKindPath, TYPE_PATH } from './type-routes';

/**
 * 지원사업 목록의 기둥 알약 — 전체 | 자금 | 융자 | 시설 | 프로그램 (짧은 이름표 `SUPPORT_TAB_LABEL`). 넘치면 옆으로 민다.
 * 머리 메뉴가 밑줄 탭이라 둘째 층인 여기는 검정 알약(pill-tab). 커뮤니티 게시판·프로필 탭과 같은 모양.
 * SSR 과 SPA 가 같은 컴포넌트를 쓰고 링크만 render prop.
 */
/** `search` 는 탭을 바꿔도 따라가는 목록 상태(정렬·지역·진행중, 기본값 제외 — list-state carrySearch). */
export type TypeTabLink = (props: { href: string; search: Record<string, string>; className: string; children: ReactNode }) => ReactNode;
/** 켜진 알약의 바탕. SSR 은 정적 span, SPA 는 `motion.span layoutId` 로 미끄러지는 span. */
export type TabHighlight = (props: { className: string }) => ReactNode;
const staticHighlight: TabHighlight = ({ className }) => <span aria-hidden className={className} />;

const ORDER: readonly GrantSupportFilter[] = GRANT_SUPPORT_FILTERS;

export function GrantTypeTabs({
  active,
  link,
  search = {},
  highlight = staticHighlight,
}: {
  active: GrantSupportFilter | null;
  link: TypeTabLink;
  search?: Record<string, string>;
  highlight?: TabHighlight;
}) {
  const tab = (on: boolean, label: string) => ({
    className: cn(PILL_TAB, on ? PILL_TAB_SLIDING_ON : PILL_TAB_OFF),
    children: on ? (<>{highlight({ className: PILL_TAB_HIGHLIGHT })}{label}</>) : label,
  });
  return (
    <div role="tablist" aria-label="지원 유형" className="flex w-full items-center gap-1 overflow-x-auto whitespace-nowrap">
      {link({ href: '/news', search, ...tab(active === null, '전체') })}
      {ORDER.map((k) => (
        <Fragment key={k}>{link({ href: TYPE_PATH[k], search, ...tab(active === k, SUPPORT_TAB_LABEL[k]) })}</Fragment>
      ))}
    </div>
  );
}

/**
 * 프로그램 탭 안의 하위 종류 알약 — 전체 | 교육·컨설팅·멘토링 | 네트워킹·행사 | … (REFACTOR §6).
 * 기둥 알약과 같은 꼴. 종류는 주소(`/news/programs/<slug>`)에 산다. SSR 과 SPA 가 같은 컴포넌트를 쓰고 링크만 render prop.
 */
export function ProgramKindTabs({
  active,
  link,
  search = {},
  highlight = staticHighlight,
}: {
  active: ProgramKind | null;
  link: TypeTabLink;
  search?: Record<string, string>;
  highlight?: TabHighlight;
}) {
  // 첫째 줄과 같은 알약이다 — 글자 · 높이 · 여백 전부 PILL_TAB 그대로 (2026-09-24 형이 맞춤).
  const tab = (on: boolean, label: string) => ({
    className: cn(PILL_TAB, on ? PILL_TAB_SLIDING_ON : PILL_TAB_OFF),
    children: on ? (<>{highlight({ className: PILL_TAB_HIGHLIGHT })}{label}</>) : label,
  });
  return (
    <div role="tablist" aria-label="프로그램 종류" className="flex w-full items-center gap-1 overflow-x-auto whitespace-nowrap">
      {link({ href: TYPE_PATH.program, search, ...tab(active === null, '전체') })}
      {PROGRAM_KINDS.map((k) => (
        <Fragment key={k}>{link({ href: programKindPath(k), search, ...tab(active === k, PROGRAM_KIND_LABEL[k]) })}</Fragment>
      ))}
    </div>
  );
}

/** 필터를 적용한 전체 건수 — 정렬 상자 오른쪽에 선다 (2026-09-24 형이 탭 줄에서 옮겼다). SSR 과 SPA 가 같은 문자열. */
export function GrantTotal({ total }: { total?: number }) {
  if (typeof total !== 'number') return null;
  return (
    <span aria-live="polite" className="shrink-0 text-xs tabular-nums text-gray-500">
      {total.toLocaleString('ko-KR')}건
    </span>
  );
}
